import { DIALER_ADMIN, DIALER_URL } from '@/Config';
import axiosInstance from '@/services/axiosInstance';
import { Agent, SuperVisor, User } from '@/types';
import { USER_TYPES } from './constants';
import { getRolePermissions } from './rolePermissions';

type DialerUser = User | Agent | SuperVisor | null;

export const getAdminDialerUrl = (): string =>
  `${DIALER_URL}/vicidial/target_admin_login.php?username=${encodeURIComponent(DIALER_ADMIN.USER_NAME)}&password=${encodeURIComponent(DIALER_ADMIN.PASSWORD)}&type=login_dashboard`;

const openSupervisorDialer = async (user: SuperVisor): Promise<void> => {
  const username = user.company?.viciDialTenantUserNumber;
  const password = user.company?.viciDialTenantUserPassword;

  if (!username || !password) {
    alert('Dialer credentials are not set for your account');
    return;
  }

  const { data: firstCheck } = await axiosInstance.get(
    `${DIALER_URL}/vicidial/target_admin_login.php`,
    { params: { username, password, type: 'user_pass_check' } },
  );

  if (firstCheck.status) {
    const url = `${DIALER_URL}/vicidial/target_admin_login.php?username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&type=login_dashboard`;
    window.open(url, '_blank');
  }
};

const openManagerDialer = async (user: SuperVisor): Promise<void> => {
  const username = user.userDetails?.viciDialAgentNumber;
  const password = user.userDetails?.viciDialAgentPassword;

  if (!username || !password) {
    alert('Dialer credentials are not set for your account');
    return;
  }

  const { data: firstCheck } = await axiosInstance.get(
    `${DIALER_URL}/vicidial/target_admin_login.php`,
    { params: { username, password, type: 'user_pass_check' } },
  );

  if (firstCheck.status) {
    const url = `${DIALER_URL}/vicidial/target_admin_login.php?username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&type=login_dashboard`;
    window.open(url, '_blank');
  }
};

const isManager = (user: DialerUser): user is SuperVisor =>
  getRolePermissions(user).isDepartmentManager;

const openAgentDialer = async (user: Agent, accessToken?: string | null): Promise<void> => {
  const phoneLogin = user.parent?.company?.viciDialTenantPhoneLogin;
  const phonePassword = user.parent?.company?.viciDialTenantPhonePassword;
  const agentLogin = user.userDetails?.viciDialAgentNumber;
  const agentPassword = user.userDetails?.viciDialAgentPassword;
  const campaignId = user.agentViciDialCampaigns?.viciDialCampaignId;

  if (!phoneLogin || !phonePassword || !agentLogin || !agentPassword || !campaignId) {
    alert('Dialer credentials are not set for your account.');
    return;
  }

  const { data: firstCheck } = await axiosInstance.get(
    `${DIALER_URL}/agc/target_agent_login.php`,
    {
      params: {
        phone_login: phoneLogin,
        phone_pass: phonePassword,
        user_login: agentLogin,
        user_password: agentPassword,
        campaign_id: campaignId,
        type: 'user_pass_check',
      },
    },
  );

  if (firstCheck.status) {
    const url = `${DIALER_URL}/agc/target_agent_login.php?` +
      `phone_login=${encodeURIComponent(agentLogin)}` +
      `&phone_pass=${encodeURIComponent(agentPassword)}` +
      `&user_login=${encodeURIComponent(agentLogin)}` +
      `&user_password=${encodeURIComponent(agentPassword)}` +
      `&campaign_id=${encodeURIComponent(campaignId)}` +
      `&webname=${encodeURIComponent(user.firstName || '')}` +
      `&wecompanyname=${encodeURIComponent(user.parent?.company?.name || '')}` +
      `&webtoken=${encodeURIComponent(accessToken || '')}` +
      '&type=login_dashboard';

    window.open(url, '_blank');
  }
};

export const openDialerForUser = async (
  user: DialerUser,
  accessToken?: string | null,
): Promise<void> => {
  if (!user) return;

  if (user.userType === USER_TYPES.SUPER_ADMIN) {
    window.open(getAdminDialerUrl(), '_blank');
    return;
  }

  if (isManager(user)) {
    await openManagerDialer(user);
    return;
  }

  if (user.userType === USER_TYPES.SUPERVISOR) {
    await openSupervisorDialer(user as SuperVisor);
    return;
  }

  if (user.userType === USER_TYPES.AGENT) {
    await openAgentDialer(user as Agent, accessToken);
  }
};
